import { AdjustmentWorkOrderPalletRole, PALLET_LABEL_PRINTABLE_STATUSES } from "./constants"
import type { AdjustmentWorkOrderPallet } from "./types"

import type { AdjustmentHistoryLot, InventoryByPalletItem } from "@/graphql/codegen/graphql"
import { roundToPrecision } from "@/utils/number"
import type { PalletLabelParams } from "@/utils/printing/generatePalletLabelZpl"

const getPalletRole = (lots: AdjustmentHistoryLot[]): AdjustmentWorkOrderPalletRole => {
  const PALLET_ROLE_EVENT_QUALIFIER = {
    SOURCE: "source",
    TARGET: "target",
  } as const

  if (lots.some((lot) => lot.event.toLowerCase().includes(PALLET_ROLE_EVENT_QUALIFIER.SOURCE))) {
    return AdjustmentWorkOrderPalletRole.Source
  }

  if (lots.some((lot) => lot.event.toLowerCase().includes(PALLET_ROLE_EVENT_QUALIFIER.TARGET))) {
    return AdjustmentWorkOrderPalletRole.Target
  }

  return AdjustmentWorkOrderPalletRole.New
}

export const toWorkOrderPallets = (lots: AdjustmentHistoryLot[]): AdjustmentWorkOrderPallet[] => {
  const PALLET_ROLE_TO_SORT_ORDER: Record<AdjustmentWorkOrderPalletRole, number> = {
    [AdjustmentWorkOrderPalletRole.Source]: 0,
    [AdjustmentWorkOrderPalletRole.Target]: 1,
    [AdjustmentWorkOrderPalletRole.New]: 2,
  }

  const groups = new Map<number | null, AdjustmentHistoryLot[]>()

  for (const lot of lots) {
    const key = lot.palletNbr ?? null
    const existing = groups.get(key)
    if (existing) {
      existing.push(lot)
    } else {
      groups.set(key, [lot])
    }
  }

  return Array.from(groups.entries())
    .map(([palletNbr, palletLots]) => ({
      palletNbr,
      role: getPalletRole(palletLots),
      location: palletLots[0].location ?? "",
      warehouseShelving: palletLots[0].warehouseShelving ?? null,
      lots: palletLots,
    }))
    .sort((a, b) => PALLET_ROLE_TO_SORT_ORDER[a.role] - PALLET_ROLE_TO_SORT_ORDER[b.role])
}

export const getPalletReferenceNumbersForPrint = (lots: AdjustmentHistoryLot[]): number[] => [
  ...new Set(
    lots
      .filter((lot) => lot.palletNbr && lot.lotStatus && PALLET_LABEL_PRINTABLE_STATUSES.includes(lot.lotStatus))
      .map((lot) => lot.palletNbr!)
  ),
]

export const toAdjustmentPalletLabelParams = ({
  referenceNbr,
  product,
  lots,
}: InventoryByPalletItem): PalletLabelParams => {
  const countByPackageQuantity = new Map<number, number>()
  let mostCommonPackageQuantity: number | null = null
  let highestPackageQuantityCount = 0
  let palletQuantity = 0
  let packageCount = 0

  for (const { lotUom, packageUom } of lots) {
    palletQuantity += lotUom ?? 0

    if (!packageUom) continue

    // TODO: AK-6348 switch to a BE-provided package count once InventoryByPalletItemLot exposes one
    packageCount += (lotUom ?? 0) / packageUom

    const count = (countByPackageQuantity.get(packageUom) ?? 0) + 1
    countByPackageQuantity.set(packageUom, count)

    if (count > highestPackageQuantityCount) {
      highestPackageQuantityCount = count
      mostCommonPackageQuantity = packageUom
    }
  }

  return {
    productName: product.companyProductDescription ?? "Unknown Product",
    referenceId: String(referenceNbr ?? "N/A"),
    stockNumber: product.publicId ?? "N/A",
    palletQuantity: String(roundToPrecision(palletQuantity, 2)),
    packageQuantity: String(mostCommonPackageQuantity ?? "N/A"),
    packageCount: Math.round(packageCount),
    lots: lots.map(({ lotNumber, batchNumber, lotUom }) => ({ lotNumber, batchNumber, quantity: lotUom })),
    uomName: product.packageUsWeightUomDetails?.name ?? "N/A",
  }
}
